import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@heroui/react'
import libphonenumber from 'google-libphonenumber'
import { useState } from 'react'

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance()

function validatePhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== 'string') {
    return { isValid: false, error: 'Phone number is required' }
  }

  try {
    let parsed
    try {
      parsed = phoneUtil.parse(phoneNumber)
    }
    catch {
      try {
        parsed = phoneUtil.parse(phoneNumber, 'US')
      }
      catch {
        return {
          isValid: false,
          error: 'Unable to parse phone number. Please include country code (e.g., +1 for US)',
        }
      }
    }

    if (!phoneUtil.isValidNumber(parsed)) {
      return { isValid: false, error: 'Invalid phone number' }
    }

    const standardized = phoneUtil.format(parsed, libphonenumber.PhoneNumberFormat.E164)
    return { isValid: true, standardized }
  }
  catch {
    return { isValid: false, error: 'Phone number validation failed' }
  }
}

export default function AddBlockModal({
  isOpen = false,
  onClose,
  inboundNumber,
  onAdd,
  isLoading = false,
}) {
  const [formData, setFormData] = useState({
    blocked_numbers: '',
    reason: '',
  })
  const [errors, setErrors] = useState({})

  const handleSubmit = () => {
    const newErrors = {}

    // Parse phone numbers (support both single and multiple)
    const numbersText = formData.blocked_numbers.trim()
    if (!numbersText) {
      newErrors.blocked_numbers = 'At least one phone number is required'
      setErrors(newErrors)
      return
    }

    // Split by comma, newline, or space and clean up
    const numbers = numbersText
      .split(/[,\s]+/)
      .map(num => num.trim())
      .filter(num => num.length > 0)

    // Validate all numbers
    const validationResults = numbers.map(num => ({
      original: num,
      ...validatePhoneNumber(num),
    }))

    const invalidNumbers = validationResults.filter(result => !result.isValid)
    if (invalidNumbers.length > 0) {
      newErrors.blocked_numbers = `Invalid numbers: ${invalidNumbers.map(inv => inv.original).join(', ')}`
      setErrors(newErrors)
      return
    }

    setErrors({})

    const validNumbers = validationResults.map(result => result.standardized)
    const isGlobal = !inboundNumber || inboundNumber === '+10000000000'

    // Always send as array (unified approach)
    onAdd({
      numbers: validNumbers,
      is_global: isGlobal,
      inbound_number: isGlobal ? null : inboundNumber,
      reason: formData.reason || null,
    })
  }

  const handleClose = () => {
    setFormData({
      blocked_numbers: '',
      reason: '',
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="2xl" radius="none">
      <ModalContent>
        <ModalHeader>Add Block Rule</ModalHeader>
        <ModalBody className="flex flex-col gap-y-4">
          <Textarea
            label="Phone Number(s)"
            placeholder="Enter phone number(s) separated by commas, spaces, or new lines&#10;Examples:&#10;+1234567890&#10;+1234567890, +0987654321&#10;+1234567890&#10;+0987654321"
            value={formData.blocked_numbers}
            onChange={e => setFormData(prev => ({ ...prev, blocked_numbers: e.target.value }))}
            isInvalid={!!errors.blocked_numbers}
            errorMessage={errors.blocked_numbers}
            minRows={3}
            variant="bordered"
            radius="none"
          />

          <Input
            label="Reason (Optional)"
            placeholder="Why is this number being blocked?"
            value={formData.reason}
            onChange={e => setFormData(prev => ({ ...prev, reason: e.target.value }))}
            variant="bordered"
            radius="none"
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onPress={handleClose} radius="none">
            Cancel
          </Button>
          <Button
            color="primary"
            onPress={handleSubmit}
            isLoading={isLoading}
            radius="none"
          >
            Add Block Rule
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
